import React, {FC, useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Row, Col, Card, Typography, Spin, Button, message, Space} from 'antd';
import partnerService from '../../services/partnerService';
import {formatString, formatValue, useIsMobile} from '../../utils/helpers';

const {Text} = Typography;

interface Partner {
    _id: string;
    username: string;
    userType: 'individual' | 'business';
    displayName: string;
    onboardedUserCount: number;
    onboardedUserRevenue: number;
    activeCampaignLinks: number;
    totalClicks: number;
    totalMoneyGenerated: number;
    averageClickRate: number;
}

const PartnerPage: FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [partner, setPartner] = useState<Partner | null>(null);
    const [loading, setLoading] = useState(true);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            try {
                const data: any = await partnerService.getPartner(id);
                setPartner(data);
            } catch (err) {
                message.error('Failed to load partner details.');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const handleViewCampaignLinks = async () => {
        if (!id) return;

        try {
            navigate(`/partners/${id}/campaign-links`);
        } catch {
            message.error('Unable to load campaign links.');
        }
    };

    if (loading) return <Spin tip="Loading…"/>;

    if (!partner) return <Text>Partner not found.</Text>;

    return (
        <div style={{maxWidth: '90%', margin: 'auto', padding: 20}}>
            <label className={"title"}>Partner details</label>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <Text strong>Username:</Text> {partner.username}<br/>
                        <Text strong>User type:</Text> {formatString(partner.userType)}<br/>
                        <Text strong>Name:</Text> {partner.displayName}<br/>
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                    <Card title="Onboarded users">{partner.onboardedUserCount}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Total clicks">{partner.totalClicks}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Active campaign links">{partner.activeCampaignLinks}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Onboarding earnings">${formatValue(partner.onboardedUserRevenue)}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Click earnings">${formatValue(partner.totalMoneyGenerated)}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Average click rate">${formatValue(partner.averageClickRate)}</Card>
                </Col>
            </Row>

            <Row justify="end" style={{marginTop: 20}}>
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        justifyContent: isMobile ? 'stretch' : 'flex-end',
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <Button
                        type="primary"
                        onClick={handleViewCampaignLinks}
                        block={isMobile}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Campaign links
                    </Button>

                    <Button
                        type="default"
                        onClick={() => navigate(-1)}
                        block={isMobile}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Back
                    </Button>
                </div>
            </Row>
        </div>
    );
};

export default PartnerPage;
